/**
 * Сводный график портфеля (ПЛ-34): полоса каждого проекта от начала до окончания по расчёту, отметка окончания
 * по базовому плану и директивного срока, вехи; ниже — конфликты загрузки людей между проектами.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { fmtMoment } from './model';

interface Portfolio {
  more: boolean;
  projects: Array<{
    projectId: string;
    code: string;
    name: string;
    scheduleId: string | null;
    start: string | null;
    finish: string | null;
    baselineFinish: string | null;
    mustFinishBy: string | null;
    milestones: Array<{
      id: string;
      code: string;
      name: string;
      date: string;
      baseline: string | null;
      critical: boolean;
    }>;
  }>;
  conflicts: Array<{
    resourceId: string;
    name: string;
    week: string;
    need: number;
    available: number;
    projects: Array<{ projectId: string; code: string; need: number }>;
  }> | null;
}
const t = (s: string) => Date.parse(`${s.slice(0, 10)}T00:00:00Z`);
const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');
const n1 = (x: number) => String(Math.round(x * 10) / 10).replace('.', ',');

export function PortfolioView() {
  const nav = useNavigate();
  const q = useQuery({
    queryKey: ['schedule-portfolio'],
    queryFn: () => api<Portfolio>('/schedule/portfolio'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const rows = q.data.projects.filter((p) => p.start && p.finish);
  if (!rows.length) return null;
  const dates = rows.flatMap((p) =>
    [p.start, p.finish, p.baselineFinish, p.mustFinishBy].filter((x): x is string => Boolean(x)).map(t),
  );
  const t0 = Math.min(...dates);
  const t1 = Math.max(...dates);
  const W = 1000;
  const L = 150;
  const RH = 34;
  const H = 30 + rows.length * RH;
  const x = (v: number) => L + ((v - t0) / Math.max(1, t1 - t0)) * (W - L - 16);
  const months: number[] = [];
  const d = new Date(t0);
  d.setUTCDate(1);
  // Не больше 24 подписей: при длинном портфеле — через месяц или квартал
  const span = Math.max(1, Math.ceil((t1 - t0) / (86400000 * 30 * 24)));
  while (d.getTime() <= t1) {
    if (d.getTime() >= t0) months.push(d.getTime());
    d.setUTCMonth(d.getUTCMonth() + span);
  }
  const today = Date.now();
  const conflicts = q.data.conflicts;
  return (
    <>
      <Panel title="Сводный график" meta="проекты, базовый план, директивный срок, вехи">
        <ScrollX>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="min-w-[720px]"
            role="img"
            aria-label="Сводный график проектов"
          >
            {months.map((m) => (
              <g key={m}>
                <line x1={x(m)} x2={x(m)} y1={18} y2={H} stroke="var(--color-line)" />
                <text x={x(m) + 3} y={13} className="fill-ink-2 text-[11px]">
                  {new Date(m).toLocaleDateString('ru-RU', {
                    month: 'short',
                    year: '2-digit',
                    timeZone: 'UTC',
                  })}
                </text>
              </g>
            ))}
            {today >= t0 && today <= t1 ? (
              <line
                x1={x(today)}
                x2={x(today)}
                y1={18}
                y2={H}
                stroke="var(--color-accent, #ff5a1f)"
                strokeDasharray="3 3"
              />
            ) : null}
            {rows.map((p, i) => {
              const y = 26 + i * RH;
              const late = p.baselineFinish && p.finish && p.finish > p.baselineFinish;
              const overdue =
                p.mustFinishBy && p.finish && p.finish.slice(0, 10) > p.mustFinishBy.slice(0, 10);
              return (
                // biome-ignore lint/a11y/useSemanticElements: строка сводного графика SVG
                <g
                  key={p.projectId}
                  role="link"
                  tabIndex={0}
                  aria-label={`${p.code} ${p.name}`}
                  className="cursor-pointer outline-none"
                  onClick={() => nav({ to: '/schedule/$projectId', params: { projectId: p.projectId } })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      nav({ to: '/schedule/$projectId', params: { projectId: p.projectId } });
                  }}
                >
                  <rect x={0} y={y - 4} width={W} height={RH} fill="transparent" />
                  <text x={4} y={y + 13} className="fill-ink text-[12px] font-medium">
                    {p.code}
                  </text>
                  <rect
                    x={x(t(p.start!))}
                    y={y + 4}
                    width={Math.max(2, x(t(p.finish!)) - x(t(p.start!)))}
                    height={12}
                    rx={3}
                    fill={overdue || late ? 'var(--color-danger)' : 'var(--color-ink-2)'}
                  >
                    <title>
                      {p.code} {p.name}: {fmtMoment(p.start)} — {fmtMoment(p.finish)}
                      {p.baselineFinish ? `, по базовому плану ${fmtMoment(p.baselineFinish)}` : ''}
                    </title>
                  </rect>
                  {p.baselineFinish ? (
                    <line
                      x1={x(t(p.baselineFinish))}
                      x2={x(t(p.baselineFinish))}
                      y1={y}
                      y2={y + 20}
                      stroke="var(--color-ink)"
                      strokeWidth={2}
                    />
                  ) : null}
                  {p.mustFinishBy ? (
                    <path
                      d={`M${x(t(p.mustFinishBy))},${y} l5,-6 h-10 z`}
                      fill="var(--color-danger)"
                      transform={`translate(0,${4})`}
                    />
                  ) : null}
                  {p.milestones.map((m) => (
                    <path
                      key={m.id}
                      d={`M${x(t(m.date))},${y + 2} l6,8 l-6,8 l-6,-8 z`}
                      fill={m.critical ? 'var(--color-danger)' : 'var(--color-ink)'}
                      stroke="var(--color-surface)"
                    >
                      <title>
                        {m.code} {m.name}: {dd(m.date)}
                        {m.baseline ? ` (по базовому плану ${dd(m.baseline)})` : ''}
                      </title>
                    </path>
                  ))}
                </g>
              );
            })}
          </svg>
        </ScrollX>
        {q.data.more ? (
          <p className="mt-2 text-[12px] text-danger">Показаны первые 60 проектов по коду.</p>
        ) : null}
        <p className="mt-2 text-[12px] text-ink-2">
          Красная полоса — окончание позже базового плана или директивного срока; чёрная черта — окончание по
          базовому плану; треугольник — директивный срок; ромбы — вехи. Нажмите на проект, чтобы открыть его
          график.
        </p>
      </Panel>
      {conflicts ? (
        <Panel title="Конфликты загрузки между проектами" meta="люди одной профессии на нескольких проектах">
          {conflicts.length ? (
            <ul className="divide-y divide-line text-[14px]">
              {conflicts.slice(0, 60).map((c) => (
                <li key={`${c.resourceId}|${c.week}`} className="flex flex-wrap justify-between gap-2 py-1.5">
                  <span>
                    <span className="font-medium">{c.name}</span>
                    <span className="text-ink-2"> · неделя с {dd(c.week)}</span>
                    <span className="block text-[12px] text-ink-2">
                      {c.projects.map((p) => `${p.code}: ${n1(p.need)}`).join(' · ')}
                    </span>
                  </span>
                  <span className="num text-danger">
                    нужно {n1(c.need)}, на вахтах {n1(c.available)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-ink-2">
              Конфликтов нет: в каждую неделю людей каждой профессии на вахтах хватает на все проекты.
            </p>
          )}
        </Panel>
      ) : null}
    </>
  );
}
